import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const Navigation = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'solution', href: '#solution', label: 'Solución' },
    { key: 'crew', href: '#crew', label: 'Equipo' },
    { key: 'contact', href: '#contact', label: 'Contacto' },
  ];

  const languages = [
    { code: 'es', label: 'Español', flag: '🇨🇱' },
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'pt', label: 'Português', flag: '🇧🇷' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-slate-950/95 backdrop-blur-md border-b border-slate-800' 
            : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.a
              href="#"
              data-testid="logo-link"
              className="flex items-center gap-3"
              whileHover={{ scale: 1.02 }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img 
                src="/images/allcom-logo.png" 
                alt="ALLCOM" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="hidden sm:block">
                <span className={`font-bold text-lg tracking-tight ${isScrolled ? 'text-white' : 'text-white'}`}>
                  ALLCOM
                </span>
                <span className={`text-xs block -mt-1 tracking-wider ${isScrolled ? 'text-slate-400' : 'text-slate-400'}`}>
                  IA TECHNOLOGIES
                </span>
              </div>
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  data-testid={`nav-${item.key}`}
                  onClick={() => scrollToSection(item.href)}
                  className={`text-sm font-medium transition-colors ${
                    isScrolled 
                      ? 'text-slate-400 hover:text-white' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="language-selector"
                    className={`flex items-center gap-2 ${
                      isScrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    <Globe className="w-4 h-4" />
                    <span className="uppercase text-xs font-medium">{language}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border border-slate-100">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      data-testid={`lang-${lang.code}`}
                      onClick={() => setLanguage(lang.code)}
                      className={`cursor-pointer ${language === lang.code ? 'bg-slate-50' : ''}`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* CTA Button */}
              <Button
                data-testid="nav-cta-btn"
                onClick={() => scrollToSection('#contact')}
                className="hidden md:flex font-semibold px-6 py-2 rounded-full bg-white text-slate-900 hover:bg-slate-100 transition-all"
              >
                Contactar
              </Button>

              {/* Mobile Menu Button */}
              <button
                data-testid="mobile-menu-btn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-white"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-slate-900 pt-24"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="p-6">
              <div className="space-y-2">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.key}
                    data-testid={`mobile-nav-${item.key}`}
                    onClick={() => scrollToSection(item.href)}
                    className="block w-full text-left py-4 text-xl font-medium text-white border-b border-slate-800"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {item.label}
                  </motion.button>
                ))}
              </div>
              <motion.div
                className="mt-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Button
                  data-testid="mobile-cta-btn"
                  onClick={() => scrollToSection('#contact')}
                  className="w-full bg-blue-600 text-white hover:bg-blue-700 py-4 text-lg font-semibold rounded-xl"
                >
                  Solicitar Diagnóstico Gratis
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
